/**
 * Vehicle Expenses Hook
 * React hook jármű költségek kezeléséhez
 */

import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { VehicleExpense } from '../types/vehicle';
import { collection, query, where, onSnapshot, orderBy } from 'firebase/firestore';
import { db } from '../lib/firebase';
import {
  getVehicleExpenses,
  getOrganizationExpenses,
  createVehicleExpense,
  updateVehicleExpense,
  deleteVehicleExpense
} from '../services/vehicleService';

export function useVehicleExpenses(vehicleId?: string, limitCount?: number) {
  const { user } = useAuth();
  const [expenses, setExpenses] = useState<VehicleExpense[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Real-time listener for expenses
  useEffect(() => {
    if (!user?.organizationId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    const expensesRef = collection(db, 'vehicleExpenses');
    const constraints: any[] = [orderBy('date', 'desc')];

    if (vehicleId) {
      constraints.unshift(where('vehicleId', '==', vehicleId));
    } else {
      constraints.unshift(where('organizationId', '==', user.organizationId));
    }

    const q = query(expensesRef, ...constraints);

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const expensesData = snapshot.docs.map(doc => ({
          ...doc.data(),
          id: doc.id,
          date: doc.data().date?.toDate() || new Date(),
          createdAt: doc.data().createdAt?.toDate() || new Date(),
          updatedAt: doc.data().updatedAt?.toDate() || new Date()
        })) as VehicleExpense[];

        // Limit alkalmazása ha van
        const limitedExpenses = limitCount ? expensesData.slice(0, limitCount) : expensesData;

        setExpenses(limitedExpenses);
        setLoading(false);
        setError(null);
      },
      (err) => {
        setError(err.message || 'Hiba a költségek betöltése során');
        console.error('Error loading vehicle expenses:', err);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [user?.organizationId, vehicleId, limitCount]);

  // Költségek betöltése (manual refresh)
  const loadExpenses = useCallback(async () => {
    if (!user?.organizationId) {
      return;
    }

    try {
      setLoading(true);
      const data = vehicleId
        ? await getVehicleExpenses(vehicleId, limitCount)
        : await getOrganizationExpenses(user.organizationId, limitCount);
      setExpenses(data);
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Hiba a költségek betöltése során');
      console.error('Error loading vehicle expenses:', err);
    } finally {
      setLoading(false);
    }
  }, [user?.organizationId, vehicleId, limitCount]);

  // Új költség létrehozása
  const create = useCallback(
    async (expenseData: Omit<VehicleExpense, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> => {
      if (!user?.organizationId || !user?.uid) {
        throw new Error('Nincs bejelentkezve');
      }

      try {
        const expenseId = await createVehicleExpense(expenseData, user.organizationId, user.uid);
        return expenseId;
      } catch (err: any) {
        setError(err.message || 'Hiba a költség létrehozása során');
        throw err;
      }
    },
    [user?.organizationId, user?.uid]
  );

  // Költség frissítése
  const update = useCallback(
    async (expenseId: string, updates: Partial<VehicleExpense>): Promise<void> => {
      try {
        await updateVehicleExpense(expenseId, updates);
      } catch (err: any) {
        setError(err.message || 'Hiba a költség frissítése során');
        throw err;
      }
    },
    []
  );

  // Költség törlése
  const remove = useCallback(async (expenseId: string): Promise<void> => {
    try {
      await deleteVehicleExpense(expenseId);
    } catch (err: any) {
      setError(err.message || 'Hiba a költség törlése során');
      throw err;
    }
  }, []);

  return {
    expenses,
    loading,
    error,
    loadExpenses,
    create,
    update,
    remove
  };
}
